import { useNavigate } from '@tanstack/react-router';
import { useSyncExternalStore } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  clearPendingProfile,
  getPendingProfile,
  subscribeToProfileReady,
} from '@/containers/agents/use-onboarding-research-watcher.hook';

/**
 * Surfaces a finished agents-onboarding profile in front of whatever the user
 * is doing.
 *
 * Agents setup kicks off a multi-minute server-side research run and sends the
 * user straight back to Home rather than parking them on a spinner, so the
 * result arrives while they're somewhere else entirely. This is the moment it
 * comes back to them: mounted once in the shell, it opens over the current
 * page the instant the profile lands.
 *
 * It shows what was built and offers to open it. It never approves the profile
 * — saving stays an explicit act on the review card.
 */
export function AgentProfileReadyDialog() {
  const navigate = useNavigate();
  const profile = useSyncExternalStore(
    subscribeToProfileReady,
    getPendingProfile,
    () => null
  );

  if (!profile) return null;

  const personas = (profile.personas ?? []).filter(Boolean).slice(0, 4);
  const summary = profile.one_liner || profile.icp || '';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) clearPendingProfile();
      }}
    >
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {profile.company_name
              ? `${profile.company_name}'s profile is ready`
              : 'Your company profile is ready'}
          </DialogTitle>
          <DialogDescription>
            Every Research and Qualification run will be tuned to this instead
            of generic B2B research.
          </DialogDescription>
        </DialogHeader>

        {(summary || personas.length > 0) && (
          <div className='space-y-3 rounded-lg border bg-muted/40 p-4 text-sm'>
            {summary && <p className='text-foreground'>{summary}</p>}
            {personas.length > 0 && (
              <div className='flex flex-wrap gap-1.5'>
                {personas.map((persona) => (
                  <span
                    key={persona}
                    className='rounded-full bg-background px-2.5 py-1 text-xs text-muted-foreground'
                  >
                    {persona}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className='gap-2 sm:justify-between'>
          <Button variant='ghost' onClick={() => clearPendingProfile()}>
            Not now
          </Button>
          <Button
            onClick={() => {
              clearPendingProfile();
              void navigate({ to: '/agents' });
            }}
          >
            Review and save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
